import React, { useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import CountUp from 'react-countup';
import { MapPinIcon, BuildingIcon, TrendingUpIcon, CoinsIcon, HomeIcon, CheckCircle2Icon, AwardIcon, HammerIcon, CalendarIcon, MapIcon, TrainIcon, LandmarkIcon, UserIcon } from 'lucide-react';
export function Overview() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  const [activeTab1, setActiveTab1] = useState('corp1');
  const [activeTab2, setActiveTab2] = useState('materiale');
  const [activeTab3, setActiveTab3] = useState('unirii');
  const stats = [{
    icon: MapPinIcon,
    label: 'Adresă',
    value: 'Str. Cuza Vodă 50–52, Sector 4, București'
  }, {
    icon: BuildingIcon,
    label: 'Suprafață construită',
    value: '2.500 mp | Teren: 395 mp'
  }, {
    icon: HomeIcon,
    label: 'Tip investiție',
    value: 'Office / Potențial reconversie rezidențială'
  }, {
    icon: TrendingUpIcon,
    label: 'Randament',
    value: 'Actual: 8–9% | Potențial revânzare: 30–35%'
  }];
  const details = [{
    label: 'Clădiri',
    value: '2 corpuri independente',
    elevation: 0
  }, {
    label: 'Structură',
    value: 'D+P+6E și S+P+7E',
    elevation: 20
  }, {
    label: 'Localizare',
    value: '850 m Piața Unirii | 1,9 km Palatul Parlamentului',
    elevation: 40
  }];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      y: 50,
      opacity: 0
    },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6
      }
    }
  };
  const tabContentVariants = {
    hidden: {
      opacity: 0,
      y: 10
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3
      }
    },
    exit: {
      opacity: 0,
      y: -10,
      transition: {
        duration: 0.2
      }
    }
  };
  const buildingData = {
    corp1: {
      floors: 6,
      label: 'D+P+6E'
    },
    corp2: {
      floors: 7,
      label: 'S+P+7E'
    }
  };
  const structureData = {
    materiale: [{
      icon: HammerIcon,
      label: 'Beton armat',
      color: '#357beb'
    }, {
      icon: CheckCircle2Icon,
      label: 'Structură rezistentă',
      color: '#10b981'
    }],
    an: {
      year: 2011
    },
    certificari: [{
      icon: AwardIcon,
      label: 'Certificat energetic A',
      color: '#f59e0b'
    }, {
      icon: CheckCircle2Icon,
      label: 'Autorizații complete',
      color: '#10b981'
    }]
  };
  const locationData = {
    unirii: {
      distance: 850,
      unit: 'm',
      time: '7-8 min'
    },
    parlament: {
      distance: 1.9,
      unit: 'km',
      time: '20 min'
    },
    metro: {
      distance: 600,
      unit: 'm',
      time: '5 min'
    }
  };
  return <section id="overview" className="py-12 md:py-24 px-4 md:px-6 lg:px-16 bg-gray-50" data-id="element-341">
      <div className="max-w-7xl mx-auto" ref={ref} data-id="element-342">
        <motion.h2 className="text-2xl md:text-3xl lg:text-5xl font-bold text-[#132c53] text-center mb-8 md:mb-12 px-4" initial={{
        y: -50,
        opacity: 0
      }} animate={inView ? {
        y: 0,
        opacity: 1
      } : {}} transition={{
        duration: 0.8
      }} data-id="element-343">
          Date <span className="text-[#357beb]" data-id="element-344">Esențiale</span>
        </motion.h2>
        {/* Price Banner */}
        <motion.div className="w-full bg-gradient-to-r from-[#357beb] to-[#132c53] rounded-xl py-8 md:py-12 mb-12 md:mb-20 shadow-2xl overflow-hidden relative" initial={{
        y: -50,
        opacity: 0
      }} animate={inView ? {
        y: 0,
        opacity: 1
      } : {}} transition={{
        duration: 0.8,
        delay: 0.2
      }} data-id="element-345">
          <div className="relative z-10 flex flex-col items-center justify-center gap-3 md:gap-4 px-4" data-id="element-346">
            <p className="text-white text-xs md:text-sm lg:text-base font-semibold tracking-wider uppercase" data-id="element-347">
              Exclusive Investment Price
            </p>
            <div className="flex items-center justify-center gap-3 md:gap-6" data-id="element-348">
              <motion.div animate={{
              scale: [1, 1.2, 1],
              opacity: [0.5, 1, 0.5]
            }} transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-349">
                <CoinsIcon className="w-6 h-6 md:w-8 md:h-8 lg:w-12 lg:h-12 text-white" data-id="element-350" />
              </motion.div>
              <motion.p className="text-white text-3xl md:text-5xl lg:text-6xl font-bold" animate={{
              scale: [1, 1.05, 1]
            }} transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-351">
                3.612.460 €
              </motion.p>
              <motion.div animate={{
              scale: [1, 1.2, 1],
              opacity: [0.5, 1, 0.5]
            }} transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-352">
                <CoinsIcon className="w-6 h-6 md:w-8 md:h-8 lg:w-12 lg:h-12 text-white" data-id="element-353" />
              </motion.div>
            </div>
          </div>
          {/* Animated background effect */}
          <div className="absolute inset-0 opacity-20" data-id="element-354">
            <motion.div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent" animate={{
            x: ['-100%', '100%']
          }} transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'linear'
          }} data-id="element-355" />
          </div>
        </motion.div>
        {/* Main Stats Grid */}
        <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8 mb-12 md:mb-20" variants={containerVariants} initial="hidden" animate={inView ? 'visible' : 'hidden'} data-id="element-356">
          {stats.map((stat, index) => <motion.div key={index} className="bg-white p-6 md:p-8 rounded-xl border border-gray-200 hover:border-[#357beb] hover:scale-105 transition-all duration-300 cursor-pointer" variants={itemVariants} data-id="element-357">
              <div className="flex flex-col gap-3 md:gap-4" data-id="element-358">
                <stat.icon className="w-6 h-6 md:w-8 md:h-8 text-[#357beb]" data-id="element-359" />
                <div data-id="element-360">
                  <p className="text-xs md:text-sm text-gray-500 mb-1 md:mb-2" data-id="element-361">
                    {stat.label}
                  </p>
                  <p className="text-base md:text-lg font-bold text-[#132c53]" data-id="element-362">
                    {stat.value}
                  </p>
                </div>
              </div>
            </motion.div>)}
        </motion.div>
        {/* New Tabbed Cards Section */}
        <motion.div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mt-12 md:mt-20" variants={containerVariants} initial="hidden" animate={inView ? 'visible' : 'hidden'} data-id="element-363">
          {/* Card 1: Clădiri */}
          <motion.div className="bg-white rounded-xl shadow-lg overflow-hidden" variants={itemVariants} data-id="element-364">
            <div className="p-4 md:p-6 border-b border-gray-100" data-id="element-365">
              <div className="flex items-center gap-2 md:gap-3" data-id="element-366">
                <BuildingIcon className="w-5 h-5 md:w-6 md:h-6 text-[#357beb]" data-id="element-367" />
                <h3 className="text-lg md:text-xl font-bold text-[#132c53]" data-id="element-368">
                  Clădiri
                </h3>
              </div>
            </div>
            <div className="flex border-b border-gray-100" data-id="element-369">
              {[{
              id: 'corp1',
              label: 'Corp 1'
            }, {
              id: 'corp2',
              label: 'Corp 2'
            }].map(tab => <button key={tab.id} onClick={() => setActiveTab1(tab.id)} className="flex-1 py-2 md:py-3 text-xs md:text-sm font-medium transition-all relative hover:scale-105 touch-manipulation" style={{
              color: activeTab1 === tab.id ? '#357beb' : '#6b7280'
            }} data-id="element-370">
                  {tab.label}
                  {activeTab1 === tab.id && <motion.div layoutId="activeTab1" className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#357beb]" transition={{
                type: 'spring',
                stiffness: 500,
                damping: 30
              }} data-id="element-371" />}
                </button>)}
            </div>
            <div className="p-4 md:p-6 h-48 md:h-64" data-id="element-372">
              <AnimatePresence mode="wait" data-id="element-373">
                <motion.div key={activeTab1} variants={tabContentVariants} initial="hidden" animate="visible" exit="exit" className="h-full flex flex-col items-center justify-center" data-id="element-374">
                  <div className="flex flex-col-reverse items-center justify-end gap-1 h-32 md:h-40 mb-3 md:mb-4" data-id="element-375">
                    {Array.from({
                    length: buildingData[activeTab1].floors
                  }).map((_, i) => <motion.div key={i} className="w-24 md:w-32 h-4 md:h-5 bg-gradient-to-r from-[#357beb] to-[#60a5fa] rounded-sm border-2 border-white shadow-sm" initial={{
                    opacity: 0,
                    x: -20
                  }} animate={{
                    opacity: 1,
                    x: 0
                  }} transition={{
                    delay: i * 0.1,
                    duration: 0.5
                  }} data-id="element-376" />)}
                  </div>
                  <p className="text-2xl md:text-3xl font-bold text-[#132c53]" data-id="element-377">
                    <CountUp end={buildingData[activeTab1].floors} duration={1} suffix=" etaje" data-id="element-378" />
                  </p>
                  <p className="text-xs md:text-sm text-gray-500 mt-1 md:mt-2" data-id="element-379">
                    {buildingData[activeTab1].label}
                  </p>
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.div>
          {/* Card 2: Structură */}
          <motion.div className="bg-white rounded-xl shadow-lg overflow-hidden" variants={itemVariants} data-id="element-380">
            <div className="p-4 md:p-6 border-b border-gray-100" data-id="element-381">
              <div className="flex items-center gap-2 md:gap-3" data-id="element-382">
                <HammerIcon className="w-5 h-5 md:w-6 md:h-6 text-[#357beb]" data-id="element-383" />
                <h3 className="text-lg md:text-xl font-bold text-[#132c53]" data-id="element-384">
                  Structură
                </h3>
              </div>
            </div>
            <div className="flex border-b border-gray-100" data-id="element-385">
              {[{
              id: 'materiale',
              label: 'Materiale'
            }, {
              id: 'an',
              label: 'An'
            }, {
              id: 'certificari',
              label: 'Certificări'
            }].map(tab => <button key={tab.id} onClick={() => setActiveTab2(tab.id)} className="flex-1 py-2 md:py-3 text-xs md:text-sm font-medium transition-all relative hover:scale-105 whitespace-nowrap px-2 touch-manipulation" style={{
              color: activeTab2 === tab.id ? '#357beb' : '#6b7280'
            }} data-id="element-386">
                  {tab.label}
                  {activeTab2 === tab.id && <motion.div layoutId="activeTab2" className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#357beb]" transition={{
                type: 'spring',
                stiffness: 500,
                damping: 30
              }} data-id="element-387" />}
                </button>)}
            </div>
            <div className="p-4 md:p-6 h-48 md:h-64" data-id="element-388">
              <AnimatePresence mode="wait" data-id="element-389">
                <motion.div key={activeTab2} variants={tabContentVariants} initial="hidden" animate="visible" exit="exit" className="h-full flex flex-col items-center justify-center gap-3 md:gap-4" data-id="element-390">
                  {activeTab2 === 'materiale' && structureData.materiale.map((item, i) => <motion.div key={i} className="flex items-center gap-2 md:gap-3 bg-gray-50 px-4 md:px-6 py-3 md:py-4 rounded-lg w-full" initial={{
                  x: -20,
                  opacity: 0
                }} animate={{
                  x: 0,
                  opacity: 1
                }} transition={{
                  delay: i * 0.1
                }} data-id="element-391">
                        <div className="p-1.5 md:p-2 rounded-lg" style={{
                    backgroundColor: `${item.color}20`
                  }} data-id="element-392">
                          <item.icon className="w-4 h-4 md:w-5 md:h-5" style={{
                      color: item.color
                    }} data-id="element-393" />
                        </div>
                        <span className="font-semibold text-[#132c53] text-sm md:text-base" data-id="element-394">
                          {item.label}
                        </span>
                      </motion.div>)}
                  {activeTab2 === 'an' && <div className="text-center" data-id="element-395">
                      <CalendarIcon className="w-10 h-10 md:w-12 md:h-12 text-[#357beb] mx-auto mb-3 md:mb-4" data-id="element-396" />
                      <p className="text-4xl md:text-5xl font-bold text-[#132c53]" data-id="element-397">
                        <CountUp end={structureData.an.year} duration={2} data-id="element-398" />
                      </p>
                      <p className="text-xs md:text-sm text-gray-500 mt-1 md:mt-2" data-id="element-399">
                        An construcție
                      </p>
                    </div>}
                  {activeTab2 === 'certificari' && structureData.certificari.map((item, i) => <motion.div key={i} className="flex items-center gap-2 md:gap-3 bg-gray-50 px-4 md:px-6 py-3 md:py-4 rounded-lg w-full" initial={{
                  x: -20,
                  opacity: 0
                }} animate={{
                  x: 0,
                  opacity: 1
                }} transition={{
                  delay: i * 0.1
                }} data-id="element-400">
                        <div className="p-1.5 md:p-2 rounded-lg" style={{
                    backgroundColor: `${item.color}20`
                  }} data-id="element-401">
                          <item.icon className="w-4 h-4 md:w-5 md:h-5" style={{
                      color: item.color
                    }} data-id="element-402" />
                        </div>
                        <span className="font-semibold text-[#132c53] text-sm md:text-base" data-id="element-403">
                          {item.label}
                        </span>
                      </motion.div>)}
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.div>
          {/* Card 3: Localizare */}
          <motion.div className="bg-white rounded-xl shadow-lg overflow-hidden" variants={itemVariants} data-id="element-404">
            <div className="p-4 md:p-6 border-b border-gray-100" data-id="element-405">
              <div className="flex items-center gap-2 md:gap-3" data-id="element-406">
                <MapPinIcon className="w-5 h-5 md:w-6 md:h-6 text-[#357beb]" data-id="element-407" />
                <h3 className="text-lg md:text-xl font-bold text-[#132c53]" data-id="element-408">
                  Localizare
                </h3>
              </div>
            </div>
            <div className="flex border-b border-gray-100" data-id="element-409">
              {[{
              id: 'unirii',
              label: 'P. Unirii'
            }, {
              id: 'parlament',
              label: 'Parlament'
            }, {
              id: 'metro',
              label: 'Metro'
            }].map(tab => <button key={tab.id} onClick={() => setActiveTab3(tab.id)} className="flex-1 py-2 md:py-3 text-xs md:text-sm font-medium transition-all relative hover:scale-105 whitespace-nowrap px-2 touch-manipulation" style={{
              color: activeTab3 === tab.id ? '#357beb' : '#6b7280'
            }} data-id="element-410">
                  {tab.label}
                  {activeTab3 === tab.id && <motion.div layoutId="activeTab3" className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#357beb]" transition={{
                type: 'spring',
                stiffness: 500,
                damping: 30
              }} data-id="element-411" />}
                </button>)}
            </div>
            <div className="p-4 md:p-6 h-48 md:h-64" data-id="element-412">
              <AnimatePresence mode="wait" data-id="element-413">
                <motion.div key={activeTab3} variants={tabContentVariants} initial="hidden" animate="visible" exit="exit" className="h-full flex flex-col items-center justify-center" data-id="element-414">
                  <div className="w-full mb-4 md:mb-6" data-id="element-415">
                    <div className="flex items-center justify-between mb-2" data-id="element-416">
                      <UserIcon className="w-5 h-5 md:w-6 md:h-6 text-[#357beb]" data-id="element-417" />
                      <span className="text-xl md:text-2xl font-bold text-[#132c53]" data-id="element-418">
                        <CountUp end={locationData[activeTab3].distance} duration={1.5} decimals={locationData[activeTab3].unit === 'km' ? 1 : 0} suffix={` ${locationData[activeTab3].unit}`} data-id="element-419" />
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 md:h-3 overflow-hidden" data-id="element-420">
                      <motion.div className="h-full bg-gradient-to-r from-[#357beb] to-[#60a5fa] rounded-full" initial={{
                      width: 0
                    }} animate={{
                      width: `${Math.min(locationData[activeTab3].distance / 2 * 100, 100)}%`
                    }} transition={{
                      duration: 1,
                      ease: 'easeOut'
                    }} data-id="element-421" />
                    </div>
                  </div>
                  <div className="text-center" data-id="element-422">
                    <p className="text-xs md:text-sm text-gray-500" data-id="element-423">
                      Timp de mers
                    </p>
                    <p className="text-lg md:text-xl font-semibold text-[#357beb]" data-id="element-424">
                      {locationData[activeTab3].time}
                    </p>
                  </div>
                  <div className="mt-3 md:mt-4" data-id="element-425">
                    {activeTab3 === 'unirii' && <MapIcon className="w-8 h-8 md:w-10 md:h-10 text-gray-300" data-id="element-426" />}
                    {activeTab3 === 'parlament' && <LandmarkIcon className="w-8 h-8 md:w-10 md:h-10 text-gray-300" data-id="element-427" />}
                    {activeTab3 === 'metro' && <TrainIcon className="w-8 h-8 md:w-10 md:h-10 text-gray-300" data-id="element-428" />}
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}